/**
 * Feature flags — the non-component half — story 0.4 AC-8, AC-9, BR-11 and BR-07.
 *
 * §5.4: **two things share one mechanism** — *modules enabled per client* (BR-07)
 * and *feature flags inside an enabled module*. Both are checked before rendering a
 * nav item, a route, or any control that spends money (BR-25's AI button).
 *
 * ⚠️ **Half of this is real.** `featureFlags` come from `/eu` since 2026-10-05; the
 * `modulo.*` flags are still a stub (§9). `useFlag('nome')` is deliberately narrow
 * so that the remaining source can change without touching a single screen.
 *
 * 🔒 A disabled module is **hidden identically to a missing permission**
 * (`PERSONAS.md`): a Cliente must not be able to infer what iOX sells to others.
 */
import { createContext, useContext } from 'react'

import { CLIENTES_STUB } from '@/tenant/clientesStub'

export type Flags = Readonly<Record<string, boolean>>

export const ContextoDeFlags = createContext<Flags>({})

/**
 * As flags do cliente em escopo: as de MÓDULO, com as `featureFlags` reais do
 * cliente por cima.
 *
 * Duas metades, ambas do `/eu`:
 *
 *  - `modulo.*` — de `modulosHabilitados` do cliente, a lista que o interruptor da
 *    `2n` grava (BR-07). Ligado desde 14/10/2026: até então era stub, e um módulo
 *    desligado continuava na barra lateral. A Administração não é módulo de cliente
 *    e fica sempre ligada. **Sem a lista** — modo mock, ou perfil ainda não
 *    carregado — vale o stub de antes, tudo ON para cliente conhecido: "a fonte não
 *    chegou" não é a mesma afirmação que "este módulo está desligado".
 *  - `featureFlags` — REAIS (decisão do Fábio, 05/10/2026). É o que faz
 *    `useFlag('assistenteIa')` valer. Ausentes no modo mock; aí vale a regra de
 *    `useFlag`: nome desconhecido conta como ligado.
 */
export function flagsDoCliente(
  idCliente: string | undefined,
  featureFlags: Flags = {},
  modulosHabilitados?: readonly string[],
): Flags {
  return { ...flagsDeModulo(idCliente, modulosHabilitados), ...featureFlags }
}

function flagsDeModulo(idCliente: string | undefined, habilitados?: readonly string[]): Flags {
  if (habilitados) {
    return {
      'modulo.finops': habilitados.includes('finops'),
      'modulo.orcamento': habilitados.includes('orcamento'),
      'modulo.compliance': habilitados.includes('compliance'),
      'modulo.admin': true,
    }
  }
  const conhecido = CLIENTES_STUB.some((cliente) => cliente.idCliente === idCliente)
  return conhecido
    ? {
        'modulo.finops': true,
        'modulo.orcamento': true,
        'modulo.compliance': true,
        'modulo.admin': true,
      }
    : {}
}

/**
 * Whether a feature is on for the client in scope.
 *
 * Defaults to **true** for an unknown name. An unregistered flag means nobody has
 * expressed an opinion, and hiding working functionality on that basis produces a
 * blank screen with no explanation — the harder failure to diagnose. Turning
 * something OFF is always an explicit act.
 */
export function useFlag(nome: string): boolean {
  return useContext(ContextoDeFlags)[nome] ?? true
}

export function useFlags(): Flags {
  return useContext(ContextoDeFlags)
}
